import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MobileAuthProvider, useMobileAuth } from './MobileAuthProvider';
import MobileDashboard from './components/MobileDashboard';
import MobileInventory from './components/MobileInventory';
import MobileSafety from './components/MobileSafety';
import MobileScopeSwitcher from './components/MobileScopeSwitcher';
import SafetyHealth from '../SafetyHealth';
import MobileBookings from './components/MobileBookings';
import MobileDelivery from './components/MobileDelivery';
import MobileEvents from './components/MobileEvents';
import MobileLogin from './components/MobileLogin';
import MobileNavigation from './components/MobileNavigation';
import MobileBoHHome from './components/MobileBoHHome';
import MobileTempLog from './components/MobileTempLog';
import MobileStockCount from './components/MobileStockCount';
import MobileAlertsAck from './components/MobileAlertsAck';
import MobileDriverHome from './components/MobileDriverHome';
import MobileNotifications from './components/MobileNotifications';
import { registerServiceWorker } from '../utils/serviceWorkerRegistration';

const ProtectedMobileRoute = ({ children, requiredFeature }) => {
  const { user, canAccessFeature } = useMobileAuth();
  
  if (!user) {
    return <Navigate to="/mobile/login" replace />;
  }
  
  if (requiredFeature && !canAccessFeature(requiredFeature)) {
    return <Navigate to="/mobile/dashboard" replace />;
  }
  
  return children;
};

const MobileAppContent = () => {
  const { user, loading } = useMobileAuth();
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);

  useEffect(() => {
    // Register service worker for PWA functionality
    registerServiceWorker();

    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsPWAInstalled(true);
    }

    // Handle PWA install prompt
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="mobile-app">
      {/* PWA Install Banner */}
      {!isPWAInstalled && window.deferredPrompt && (
        <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold text-sm">R</span>
            </div>
            <div>
              <p className="text-sm font-medium">Install Restaurant Platform</p>
              <p className="text-xs opacity-90">Get the full mobile experience</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.deferredPrompt.prompt()}
              className="px-3 py-1 bg-white text-blue-600 rounded text-sm font-medium"
            >
              Install
            </button>
            <button
              onClick={() => {
                window.deferredPrompt = null;
              }}
              className="text-white opacity-75"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main App */}
      <div className="flex flex-col h-screen">
        {user && <MobileScopeSwitcher />}
        {/* Routes */}
        <div className="flex-1 overflow-y-auto">
          <Routes>
            {/* Public Routes */}
            <Route path="/mobile/login" element={<MobileLogin />} />
            
            {/* Protected Routes */}
            <Route path="/mobile/dashboard" element={
              <ProtectedMobileRoute>
                <MobileDashboard />
              </ProtectedMobileRoute>
            } />
            
            <Route path="/mobile/inventory" element={
              <ProtectedMobileRoute requiredFeature="inventory">
                <MobileInventory />
              </ProtectedMobileRoute>
            } />
            
            <Route path="/mobile/bookings" element={
              <ProtectedMobileRoute requiredFeature="bookings">
                <MobileBookings />
              </ProtectedMobileRoute>
            } />
            
            <Route path="/mobile/delivery" element={
              <ProtectedMobileRoute requiredFeature="delivery">
                <MobileDelivery />
              </ProtectedMobileRoute>
            } />
            
            <Route path="/mobile/safety" element={
              <ProtectedMobileRoute requiredFeature="safety">
                <MobileSafety />
              </ProtectedMobileRoute>
            } />

            <Route path="/mobile/safety/inspections" element={
              <ProtectedMobileRoute requiredFeature="safety">
                <SafetyHealth />
              </ProtectedMobileRoute>
            } />
            
            <Route path="/mobile/events" element={
              <ProtectedMobileRoute requiredFeature="events">
                <MobileEvents />
              </ProtectedMobileRoute>
            } />

            {/* Phase E — Offline-first PWA, BoH role */}
            <Route path="/mobile/boh" element={
              <ProtectedMobileRoute>
                <MobileBoHHome />
              </ProtectedMobileRoute>
            } />
            <Route path="/mobile/boh/temperature" element={
              <ProtectedMobileRoute>
                <MobileTempLog />
              </ProtectedMobileRoute>
            } />
            <Route path="/mobile/boh/stock-count" element={
              <ProtectedMobileRoute>
                <MobileStockCount />
              </ProtectedMobileRoute>
            } />

            {/* Phase F — Mobile alerting ack (offline-queued) */}
            <Route path="/mobile/alerts" element={
              <ProtectedMobileRoute>
                <MobileAlertsAck />
              </ProtectedMobileRoute>
            } />

            <Route path="/mobile/notifications" element={
              <ProtectedMobileRoute>
                <MobileNotifications />
              </ProtectedMobileRoute>
            } />

            {/* Phase G — Driver PWA (job cards, navigate, status pings) */}
            <Route path="/mobile/driver" element={
              <ProtectedMobileRoute>
                <MobileDriverHome />
              </ProtectedMobileRoute>
            } />
            
            {/* Default redirect */}
            <Route path="/mobile" element={<Navigate to="/mobile/dashboard" replace />} />
            <Route path="/" element={<Navigate to="/mobile/dashboard" replace />} />
          </Routes>
        </div>

        {/* Bottom Navigation */}
        {user && <MobileNavigation />}
      </div>
    </div>
  );
};

const MobileApp = () => {
  return (
    <Router>
      <MobileAuthProvider>
        <MobileAppContent />
      </MobileAuthProvider>
    </Router>
  );
};

export default MobileApp;
